<script setup>
import {ref, reactive, computed, watch, onMounted, toRaw, onBeforeUnmount, provide} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {useUserStore} from "@/stores/user.js";
import {numeric, required, maxLength, helpers} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import Tooltip from "@/components/Tooltip/index.vue";
import Loading from "@/components/Icon/Loading.vue";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Transfer from "@/views/orders/Details/Transfer.vue";
import Logs from "@/views/orders/Details/Logs.vue";
import {Button, InputGroup, InputGroupAddon, InputNumber, InputText, Textarea} from "primevue";
import ShopStatus from "@/views/orders/Shared/ShopState.vue";
import HotOrders from "@/views/orders/Details/CurrencySell/HotOrders.vue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import UpdateHotOrder from "@/views/orders/Details/CurrencySell/UpdateHotOrder.vue";
import AddHotOrder from "@/views/orders/Details/CurrencySell/AddHotOrder.vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import HotOrderLog from "@/views/orders/Shared/HotOrderLog.vue";
import CustomerProfile from "@/components/CustomerProfile/index.vue";
import {useCustomerStore} from "@/stores/customer.js";
import Customers from "@/components/Customers.vue";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)
const showTransfer = ref(false)

/* attachments */
const attachments = ref(null)

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

/* ---------- Initial order values (to retrieve) ---------- */
const initialGame = ref('')
const initialServer = ref('')
const initialAccount = ref('')

/* ---------- Stores ---------- */
const route = useRoute()
const router = useRouter()
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const userStore = useUserStore();
const customerStore = useCustomerStore()

/* ---------- Loading status ---------- */
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const loadingHotOrders = ref(false)
const isUploadingAttachment = ref(false)
const orderNotFound = ref(false)

/* ---------- related to hot orders ---------- */
const isShowUpdateHotOrderModal = ref(false)
const isShowAddHotOrderModal = ref(false)
const isShowHotOrderLog = ref(false)
const hotOrders = ref([]);
const selectedHotOrder = ref({})

const canUpdateOrder = computed(() => userStore.hasPermission("ORDERS_CURRENCY_UPDATE") || userStore.hasPermission('BIG_BOSS'))
const canRemoveOrder = computed(() => userStore.hasPermission('ORDERS_CURRENCY_REMOVE') || userStore.hasPermission('BIG_BOSS'))
const canFinalizedOrder = computed(() => userStore.hasPermission('FINALIZE_ORDERS') || userStore.hasPermission('BIG_BOSS'))

function showUpdateHotOrderModal(emittedValue) {
  isShowUpdateHotOrderModal.value = true
  selectedHotOrder.value = emittedValue
}

function closeUpdateHotOrderModal() {
  isShowUpdateHotOrderModal.value = false
}

function showAddHotOrderModal() {
  isShowAddHotOrderModal.value = true
}

function closeAddHotOrderModal() {
  isShowAddHotOrderModal.value = false
}

function showHotOrderLog(data) {
  isShowHotOrderLog.value = true
  selectedHotOrder.value = data
}

function closeHotOrderLog() {
  selectedHotOrder.value = {}
  isShowHotOrderLog.value = false
}

/* ---------- Initial version of the order for comparison ---------- */
let initialOrder = {}   // Simple object (non-reactive)

/* ---------- creation time of order ---------- */
let creationTime = 0

/* ---------- Order object ---------- */
const order = reactive({
  state: '',
  store: '',
  game: '',
  server: '',
  account: '',
  title: '',
  amount: '',
  g2gSellID: '',
  link: '',
  price: '',
  takersCut: '',
  tax: '',
  commissionFee: '',
  withdrawalFee: '',
  income: '',
  revenue: '',
  customer: '',
  customerID: 0,
  takers: '',
  notes: '',
  id: '',
  uuid: '',
  logs: '',
  finalized: null
})

const rules = computed(() => {
  return {
    state: {required},
    store: {required},
    game: {required},
    server: {required},
    account: {required},
    title: {required},
    amount: {required, numeric},
    price: {required, numeric},
    takersCut: {required, numeric},
    tax: {required, numeric},
    customerID: {required},
  };
});
const transferRules = computed(() => {
  return {
    game: {required},
    server: {required},
    account: {required},
    amount: {required, numeric},
    price: {required, numeric},
  };
});
const $vulidate = useVuelidate(rules, order);
const $transferVulidate = useVuelidate(transferRules, order);

const fetchingCustomerData = ref(false)
const selectedCustomer = ref({})
const isShowCustomerProfileModal = ref(false);
const canChangeCustomer = ref(false)
const canShowCustomerProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))

/* ---------- Related to server ---------- */
const isShowServersModal = ref(false)

function openServersModal() {
  if (servers.value.length > 0) {
    isShowServersModal.value = true
  }
}

function closeServersModal() {
  isShowServersModal.value = false
}

function selectServer(server) {
  order.server = server
  closeServersModal()
}

/* ---------- Related to state ---------- */
const isStateOpen = ref(false)
const stateDropdownRef = ref(null)
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const toggleStateDropdown = () => isStateOpen.value = !isStateOpen.value

const selectState = (state) => {
  order.state = state
  isStateOpen.value = false
}

const handleClickOutside = (e) => {
  if (stateDropdownRef.value && !stateDropdownRef.value.contains(event.target)) isStateOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

/* ---------- Select options ---------- */
const stores = computed(() => configStore.getStores || [])

/* ---------- Selected store ---------- */
const selectedStoreObj = computed(() => stores.value.find(a => a.store === order.store) || null)

const games = computed(() => configStore.getGamesByService('Currency') || [])

const selectedGame = computed(() => games.value.find(g => g.game === order.game) || null)

const servers = computed(() => {
  if (!selectedGame.value) return []

  const servers = selectedGame.value.servers

  if (!servers.includes('All')) {
    return servers
  }

  return [
    'All',
    ...servers.filter(server => server !== 'All')
  ]
})

const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() => accounts.value.find(a => a.id === order.account) || null)

const accountTitle = computed(() => selectedAccountObj.value ? selectedAccountObj.value.title : '')

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* ---------- Income and profit calculations ---------- */
const recalc = () => {
  const price = Number(order.price)
  const tax = Number(order.tax)
  const takersCut = Number(order.takersCut)

  order.commissionFee = selectedStoreObj.value?.commissionFee ?? 0
  order.withdrawalFee = selectedStoreObj.value?.withdrawalFee ?? 0

  /* -------- income -------- */
  if (order.store && price) {
    const incomeNum = ((price * (1 - Number(order.commissionFee))) - tax) * (1 - Number(order.withdrawalFee))
    order.income = incomeNum.toFixed(2)   // ← به صورت «رشته» ذخیره می‌کنیم
  } else {
    order.income = ''
  }

  /* -------- revenue -------- */
  if (order.income !== '' && order.takersCut !== '' && !isNaN(takersCut)) {
    const revenueNum = Number(order.income) - takersCut
    order.revenue = revenueNum.toFixed(2) // ← رشته
  } else {
    order.revenue = ''
  }
}

/* ---------- Always stay up to date! ---------- */
watch(
    [() => order.store, () => order.price, () => order.takersCut, () => order.tax],
    recalc,
    {immediate: true}          // Immediately after mount and every time it changes
)

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  /* ---------- 3‑a) If the same initial Game is selected ---------- */
  if (order.game === initialGame.value) {
    // Server recovery
    if (servers.value.includes(initialServer.value)) {
      order.server = initialServer.value
    } else if (servers.value.length === 1) {
      order.server = servers.value[0]
    } else {
      order.server = ''
    }

    // Account recovery
    if (accounts.value.some(a => a.id === initialAccount.value)) {
      order.account = initialAccount.value
    } else if (accounts.value.length === 1) {
      order.account = accounts.value[0].id
    } else {
      order.account = ''
    }
    return
  }

  /* ---------- 3‑b) New Game (non-primary) ---------- */
  order.server =
      servers.value.length === 1 ? servers.value[0] : ''
  order.account =
      accounts.value.length === 1 ? accounts.value[0].id : ''
}

/* ---------- Format / Parse ---------- */
const formatText = (text) => {
  return text?.replace(/\n/g, '<br>') || ''
}

/* ---------- Initial fetch ---------- */
async function fetchInitialData() {
  loading.value = true
  closeUpdateHotOrderModal()
  closeAddHotOrderModal()
  // 1) All GameAccounts
  await ordersStore.action_getGameAccounts()

  // 2) Order details
  const data = await ordersStore.action_get_order('Currency', route.params.id)
  if (data === false) {
    orderNotFound.value = true;
    loading.value = false
    return
  }
  Object.assign(order, data)
  hotOrders.value = data.hotOrders ?? []
  creationTime = data.creationTime;

  // Save initial values
  initialGame.value = order.game
  initialServer.value = order.server
  initialAccount.value = order.account

  // 2) Save the original full version to detect changes
  initialOrder = JSON.parse(JSON.stringify(order))

  recalc()
  if (!order.customer && !order.customerID) {
    canChangeCustomer.value = true
  }

  if (order.customerID) {
    await getCustomer();
  }
  loading.value = false
}

const getCustomer = async () => {
  fetchingCustomerData.value = true
  try {
    const res2 = await customerStore.action_getCustomer({id: order.customerID})
    if (res2.result === true) {
      selectedCustomer.value = res2.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res2, life: 1500});
      //showMessage(res2, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    fetchingCustomerData.value = false
  }
}

/* ---------- Get Hot Orders ---------- */
async function getHotOrders() {
  loadingHotOrders.value = true
  closeUpdateHotOrderModal()
  closeAddHotOrderModal()
  const result = await ordersStore.action_getHotOrders_currencySell({orderID: order.id})
  if (Array.isArray(result)) {
    hotOrders.value = result
  }
  console.log(hotOrders.value)
  loadingHotOrders.value = false
}

/* ---------- Deep comparison function ---------- */
function diffDeep(curr, base) {
  if (Object.is(curr, base)) return null

  if (
      typeof curr !== 'object' || curr === null ||
      typeof base !== 'object' || base === null
  ) {
    return curr                    // Some primitive or different type
  }

  const out = Array.isArray(curr) ? [] : {}
  let hasDiff = false

  const keys = Object.keys(curr)     // Only current keys (order object)
  keys.forEach(key => {
    const sub = diffDeep(curr[key], base[key])
    if (sub !== null) {
      out[key] = sub
      hasDiff = true
    }
  })

  return hasDiff ? out : null
}

/* ---------- Detect changed fields ---------- */
function getChangedFields() {
  // Use toRaw to output Reactive Proxy
  const rawCurr = toRaw(order)
  const diff = diffDeep(rawCurr, initialOrder) || {}

  // If diff was empty, no changes
  return {id: order.id, ...diff}   // id should always be
}

const showStocksManagerBtn = computed(() =>
    selectedGame.value?.hasStocksManagerService === true
)

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  const changed = getChangedFields()

  if (attachments.value.length) {
    changed.attachments = {
      paymentReceipts: attachments.value
    }
  }

  // If it's just id, we haven't changed anything
  if (Object.keys(changed).length === 1) {
    showMessage('Nothing Changed', 'warning');
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are updating order', 'Are you sure?', 'Yes', 'No');
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.updateOrder('Currency', changed)

    if (res === true) {
      showMessage('Order Updated', 'success');
      // After success, synchronize the initial version with the current state
      initialOrder = JSON.parse(JSON.stringify(toRaw(order)))
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('currencySellOrders');
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // User verification
  const ok = await showAlert('You are deleting order', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  // 4) API call
  try {
    deleting.value = true
    const res = await ordersStore.removeOrder('Currency', {id: order.id})

    if (res === true) {
      showMessage('Order Deleted', 'success');
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('currencySellOrders');
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    deleting.value = false
  }
}

function backToOrders() {
  router.push({name: 'currency-sell-orders'})
}

async function onSendToStocksManager() {
  const isValid = await $transferVulidate.value.$validate(); // بهتر از $touch + $invalid

  if (!isValid) {
    return false;
  }

  showTransfer.value = true;
  history.pushState({modal: true}, '') // فقط به history اضافه می‌کنه
}

function closeModal(added) {
  showTransfer.value = false
  if (history.state?.modal) {
    history.back() // فقط یه مرحله می‌ره عقب
  }
  if (added) {
    order.notes = `${order.notes}\n\nAdded to Stock Manager`
  }
}

function onPopState() {
  if (showTransfer.value) {
    showTransfer.value = false
  }
}

const getSelectedCustomer = (customer) => {
  selectedCustomer.value = customer || null
  order.customerID = customer ? customer.id : null
}

function showCustomerProfileModal(user) {
  //if (user.isLoading) return;
  if (canShowCustomerProfile.value) {
    customerStore.setSelectedCustomer(user);
    isShowCustomerProfileModal.value = true
  }
}

const changeFinalized = () => {
  if (order.finalized) {
    order.finalized = false
  } else {
    order.finalized = true
  }
}

const onTakersCutInput = (e) => {
  order.takersCut = e.value
};

onMounted(() => {
  window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
})

onMounted(fetchInitialData)

/* ---------- Watch for Game Change ---------- */
watch(
    () => order.game,
    () => onGameChange()
)

provide('isShowCustomerProfileModal', isShowCustomerProfileModal)
</script>

<template>
  <Transfer v-if="showTransfer" @close="closeModal" :order="order" orderType="currencySell"></Transfer>
  <div v-else class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>
        <div v-else-if="orderNotFound" class="flex flex-col gap-2 items-center justify-center h-60 text-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" viewBox="0 0 24 24">
            <path d="M0 0h24v24H0z" fill="none" />
            <g class="alert-outline">
              <g fill="currentColor" class="Vector">
                <path fill-rule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10m-10 8a8 8 0 1 0 0-16a8 8 0 0 0 0 16" clip-rule="evenodd" />
                <path fill-rule="evenodd" d="M12 14a1 1 0 0 1-1-1V8a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1" clip-rule="evenodd" />
                <path d="M11 16a1 1 0 1 1 2 0a1 1 0 0 1-2 0" />
              </g>
            </g>
          </svg>
          Order Not Found
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="flex flex-col gap-2 h-full">
          <div class="flex w-full">
            <div class="grid grid-cols-12 lg:grid-cols-5 gap-4 h-full w-full">
              <div
                  class="col-span-12 lg:col-span-4 grid grid-cols-12 lg:flex flex-row gap-4 justify-between items-center">
                <div class="col-span-12 flex flex-row gap-2 items-center">
                  <Tooltip placement="top" arrow>
                    <template #button>
                      <div class="back-btn" @click="backToOrders">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                             class="w-4 h-4 2xl:w-6 2xl:h-6">
                          <path fill="currentColor"
                                d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                        </svg>
                      </div>
                    </template>
                    <span>{{ $t('message.back') }}</span>
                  </Tooltip>
                  <TabList class="">
                    <Tab
                        v-slot="{ selected }"
                        as="template"
                        v-for="(item, i) in tabs"
                        :key="i"
                    >
                      <button
                          :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                          class="text-xs 2xl:text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-4 2xl:px-6 py-1 2xl:py-2 transition duration-150 rounded-none first:rounded-l-2xl last:rounded-r-2xl border-r last:border-r-0 border-slate-500"
                      >
                        {{ item.title }}
                      </button>
                    </Tab>
                  </TabList>
                </div>
                <div class="col-span-12 grid grid-cols-12 lg:flex flex-row items-center gap-2">
                  <!-- "Send to Stocks Manager" button -->
                  <template
                      v-if="userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('STOCKS_MANAGER_ACCESS')">
                    <div v-if="showStocksManagerBtn"
                         class="col-span-12 flex flex-row items-center gap-1 h-[24px] lg:h-[32px] w-full lg:w-max">
                      <button @click="onSendToStocksManager"
                              class="btn rounded-2xl py-0 lg:py-2 px-4 bg-slate-700 hover:bg-primary-800 text-xs font-normal h-full w-full lg:w-max">
                        Send to Stocks Manager
                      </button>
                    </div>
                  </template>
                  <div class="col-span-12 grid grid-cols-12 lg:flex gap-4 lg:gap-2 items-center">
                    <div v-if="canFinalizedOrder" @click="changeFinalized"
                         class="col-span-6 flex gap-2 items-center rounded-md h-[32px] 2xl:h-[40px] text-xs 2xl:text-sm px-2 py-1 border cursor-pointer select-none"
                         :class="[order.finalized ? 'bg-primary-700 border-transparent' : 'bg-transparent border-slate-700']">
                      <div class="flex items-center size-4 border rounded-sm"
                           :class="[order.finalized ? 'border-slate-400' : 'border-slate-700']">
                        <svg v-if="order.finalized" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round"
                             class="lucide lucide-check-icon lucide-check">
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      Finalized
                    </div>
                    <div class="inline-block w-full lg:w-max relative col-span-6" ref="stateDropdownRef">
                      <button @click="toggleStateDropdown"
                              :class="{ '!border !border-red-300': $vulidate.state.$error }"
                              class="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md px-2 text-left flex justify-between items-center flex-row gap-2 h-[32px] lg:h-[40px]">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[order.state]"></span>
                          {{ order.state }}
                        </div>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      <div v-if="isStateOpen"
                           class="absolute right-0 z-10 mt-1 w-max min-w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-md shadow-lg">
                        <ul>
                          <li v-for="state in states" :key="state" @click="selectState(state)"
                              class="px-2 py-2 hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full" :class="stateColorsMap[state]"></span>
                            {{ state }}
                          </li>
                        </ul>
                      </div>
                      <template v-if="$vulidate.state.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.state.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-12 lg:col-span-1 flex flex-row justify-between">
                <div class="flex-1 flex flex-col gap-0.5 text-xs justify-center pl-3">
                  <span>UUID: {{ order.uuid }}</span>
                  <span>Creation Time: {{ gregorianDate(creationTime, 'DD MMM YYYY HH:mm') }}</span>
                </div>
                <div class="flex items-start">
                  <Logs :order="order"/>
                </div>
              </div>
            </div>
          </div>
          <div class="lg:flex-1 lg:grid grid-cols-5 gap-4 lg:h-full">
            <div class="col-span-4 flex flex-col relative h-full overflow-hidden">
              <hr class="border-0 h-0.5 bg-slate-800">
              <div class="flex flex-col gap-2 2xl:gap-4 relative h-full overflow-hidden">
                <TabPanel :unmount="false"
                          class="col-span-4 flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-4">
                  <div class="grid grid-cols-12 lg:flex flex-row items-start gap-4">
                    <!-- ================== Store ================== -->
                    <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm lg:w-max">
                      <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vulidate.store.$error }"
                                  variant="on">
                        <Select v-model="order.store" inputId="store" :options="stores" optionLabel="store"
                                option-value="store" overlay-class="!z-[900]"
                                class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                :class="[$vulidate.store.$error ? '!border !border-red-300' : '!border-slate-700']"
                                input-class="!text-xs 2xl:!text-sm"
                                filter autoFilterFocus/>
                        <label for="store" class="!bg-slate-900">Store</label>
                      </FloatLabel>
                      <template v-if="$vulidate.store.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.store.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <!-- ================== Game ================== -->
                    <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm lg:w-max">
                      <FloatLabel class="w-full custom-select"
                                  :class="{ 'has-error': $vulidate.game.$error || $transferVulidate.game.$error }"
                                  variant="on">
                        <Select v-model="order.game" inputId="game" :options="games" optionLabel="game"
                                option-value="game" overlay-class="!z-[900]"
                                class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                :class="[$vulidate.game.$error || $transferVulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                input-class="!text-xs 2xl:!text-sm"
                                filter autoFilterFocus/>
                        <label for="game" class="!bg-slate-900">Game</label>
                      </FloatLabel>
                      <template v-if="$vulidate.game.$error || $transferVulidate.game.$error">
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.game.required.$response || !$transferVulidate.game.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <!-- ================== Server ================== -->
                    <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel class="w-full custom-select"
                                  :class="{ 'has-error': $vulidate.server.$error || $transferVulidate.server.$error }"
                                  variant="on">
                        <Select v-model="order.server" inputId="server" :options="servers" overlay-class="!z-[900]"
                                class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                :class="[$vulidate.server.$error || $transferVulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                                input-class="!text-xs 2xl:!text-sm"
                                :virtualScrollerOptions="{ itemSize: 38 }"
                                filter autoFilterFocus/>
                        <label for="server" class="!bg-slate-900">Server</label>
                      </FloatLabel>
                      <template v-if="$vulidate.server.$error || $transferVulidate.server.$error">
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.server.required.$response || !$transferVulidate.server.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <!-- ================== Account ================== -->
                    <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm lg:w-max">
                      <FloatLabel class="w-full custom-select"
                                  :class="{ 'has-error': $vulidate.account.$error || $transferVulidate.account.$error }"
                                  variant="on">
                        <Select v-model="order.account" inputId="account" :options="accounts" optionLabel="title"
                                option-value="id" overlay-class="!z-[900]"
                                class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                :class="[$vulidate.account.$error || $transferVulidate.account.$error ? '!border !border-red-300' : '!border-slate-700']"
                                input-class="!text-xs 2xl:!text-sm"
                                :virtualScrollerOptions="{ itemSize: 32 }"
                                filter autoFilterFocus/>
                        <label for="account" class="!bg-slate-900">Account</label>
                      </FloatLabel>
                      <template v-if="$vulidate.account.$error || $transferVulidate.account.$error">
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.account.required.$response || !$transferVulidate.account.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                  <div class="grid grid-cols-12 gap-4">
                    <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel variant="on">
                        <InputText id="title" v-model="order.title" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error': $vulidate.title.$error }"/>
                        <label for="title" class="!bg-slate-900"
                               :class="{ 'focus:text-red-300': $vulidate.title.$error }">Title</label>
                      </FloatLabel>
                      <template v-if="$vulidate.title.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.title.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <div class="col-span-12 lg:col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel variant="on">
                        <InputText id="amount" v-model="order.amount" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error': $vulidate.amount.$error || $transferVulidate.amount.$error }"
                                   @keydown="allowDigitsDot"/>
                        <label for="amount" class="!bg-slate-900"
                               :class="{ 'has-error': $vulidate.amount.$error || $transferVulidate.amount.$error }">Amount</label>
                      </FloatLabel>
                      <template v-if="$vulidate.amount.$error || $transferVulidate.amount.$error">
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.amount.required.$response || !$transferVulidate.amount.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.amount.numeric.$response || !$transferVulidate.amount.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <div class="col-span-12 lg:col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel variant="on">
                        <InputText id="g2gSellID" v-model="order.g2gSellID" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                        <label for="g2gSellID" class="!bg-slate-900">Sell ID</label>
                      </FloatLabel>
                    </div>
                    <div class="col-span-12 lg:col-span-6 grid grid-cols-12 gap-4 lg:flex flex-row lg:gap-2 w-full">
                      <div class="col-span-12 lg:flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="link" v-model="order.link" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                            <label for="link" class="!bg-slate-900">Link</label>
                          </FloatLabel>
                          <Button class="!bg-transparent !border-slate-700 border-l-0 !text-xs !px-2 !py-0">
                            <a :href="order?.link" target="_blank"
                               class="text-blue-400 whitespace-nowrap hover:bg-slate-800 px-2 rounded-xl py-1">
                              Open Link
                            </a>
                          </Button>
                        </InputGroup>
                      </div>
                      <ShopStatus :state="order.shopState"/>
                    </div>
                    <div class="col-span-12 lg:col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <InputGroup>
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.price.$error || $transferVulidate.price.$error }">
                          <InputText id="price" v-model="order.price" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.price.$error || $transferVulidate.price.$error }"
                                     @keydown="allowDigitsDot"/>
                          <label for="price" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.price.$error || $transferVulidate.price.$error }">Price</label>
                        </FloatLabel>
                        <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">$
                        </InputGroupAddon>
                      </InputGroup>
                      <template v-if="$vulidate.price.$error || $transferVulidate.price.$error">
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.price.required.$response || !$transferVulidate.price.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs"
                           v-if="!$vulidate.price.numeric.$response || !$transferVulidate.price.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <div class="col-span-12 lg:col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <InputGroup>
                        <FloatLabel variant="on" :class="{ 'has-error': $vulidate.takersCut.$error }">
                          <InputNumber id="takersCut" v-model="order.takersCut"
                                       autocomplete="off" :maxFractionDigits="2"
                                       :input-class="['!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700',
                                     { 'has-error has-addon': $vulidate.takersCut.$error }
                                   ]"
                                       @input="onTakersCutInput"/>
                          <label for="takersCut" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.takersCut.$error }">Takers Cut</label>
                        </FloatLabel>
                        <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">$
                        </InputGroupAddon>
                      </InputGroup>
                      <template v-if="$vulidate.takersCut.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.takersCut.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.takersCut.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <div class="col-span-12 lg:col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <InputGroup>
                        <FloatLabel variant="on" :class="{ 'has-error': $vulidate.tax.$error }">
                          <InputText id="tax" v-model="order.tax" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.tax.$error }"
                                     @keydown="allowDigitsDot"/>
                          <label for="tax" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.tax.$error }">Tax</label>
                        </FloatLabel>
                        <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">$
                        </InputGroupAddon>
                      </InputGroup>
                      <template v-if="$vulidate.tax.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <div class="col-span-12 lg:col-span-4 flex flex-col gap-1 text-xs 2xl:text-sm">
                      <div class="flex flex-row gap-2 items-center text-xs">
                        <span class="text-[4px]">⬤</span>
                        <p class="whitespace-nowrap">Commission Fee:</p>
                        <div v-if="selectedStoreObj?.commissionFee" class="w-full">
                          {{
                            selectedStoreObj?.commissionFee ? (selectedStoreObj.commissionFee) * 100 + ' %' : '&nbsp'
                          }}
                        </div>
                        <div v-else class="w-full">
                          Store not selected
                        </div>
                      </div>
                      <div class="flex flex-row gap-2 items-start text-xs">
                        <span class="text-[4px]">⬤</span>
                        <p class="text-xs whitespace-nowrap">Withdrawal Fee:</p>
                        <div v-if="selectedStoreObj?.withdrawalFee" class="w-full">
                          {{
                            selectedStoreObj?.withdrawalFee ? (selectedStoreObj.withdrawalFee) * 100 + ' %' : '&nbsp'
                          }}
                        </div>
                        <div v-else class="w-full">
                          Store not selected
                        </div>
                      </div>
                    </div>
                    <div class="col-span-12 lg:col-span-8 flex flex-col gap-1 text-xs 2xl:text-sm">
                      <div v-if="order?.income" class="flex flex-row gap-2 items-start text-xs">
                        <span class="text-[4px]">⬤</span>
                        <p class="text-xs">Income:</p>
                        <div class="w-full">
                          {{ order?.income ? '$ ' + order.income : '&nbsp' }}
                        </div>
                      </div>
                      <div v-if="order?.revenue" class="flex flex-row gap-2 items-start text-xs">
                        <span class="text-[4px]">⬤</span>
                        <p class="text-xs">Revenue:</p>
                        <div class="w-full">
                          {{ order?.revenue ? '$ ' + order.revenue : '&nbsp' }}
                        </div>
                      </div>
                    </div>
                    <div class="col-span-12 grid grid-cols-12 gap-4">
                      <template v-if="!canChangeCustomer">
                        <div v-if="order.customerID && !fetchingCustomerData"
                             class="col-span-12 lg:col-span-4 order-2 lg:order-1 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[40px] 2xl:h-[50px]">
                          <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                               :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                            <div
                                class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="currentColor"
                                      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-4 text-xs 2xl:text-sm">
                            <div class="flex flex-col justify-between">
                              <div class="flex flex-row gap-6">
                                <div class="flex-1 truncate">{{
                                    selectedCustomer.name || selectedCustomer.storeID
                                  }}
                                </div>
                                <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                                </div>
                              </div>
                              <div class="text-slate-400 overflow-hidden text-ellipsis whitespace-nowrap">
                                <span>{{ selectedCustomer.discordUsername ?? "" }}</span>
                                <span>
                                {{
                                    (selectedCustomer.discordDisplayName ? "&nbsp- " + selectedCustomer.discordDisplayName : "")
                                  }}
                              </span>
                              </div>
                            </div>
                            <div class="flex flex-col justify-between">
                              <div>{{ selectedCustomer.verificationState ?? "" }}</div>
                              <div class="truncate">
                                {{
                                  selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else
                             class="col-span-12 lg:col-span-4 order-2 lg:order-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <FloatLabel variant="on">
                            <InputText id="customer" :value="order.customer" autocomplete="off" readonly disabled
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
                            <label for="customer" class="!bg-slate-900">Customer</label>
                          </FloatLabel>
                        </div>
                      </template>
                      <div v-else
                           :class="['col-span-12 grid grid-cols-12 gap-4', selectedCustomer?.id ? 'lg:col-span-12' : 'lg:col-span-4']">
                        <div
                            :class="['col-span-12 flex flex-row gap-2 text-xs 2xl:text-sm', selectedCustomer?.id ? 'lg:col-span-4' : 'lg:col-span-12']">
                          <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                            <Customers v-model="selectedCustomer" @update:modelValue="getSelectedCustomer"
                                       appendTo="body"
                                       label="Customer" :required="true"/>
                          </div>
                        </div>
                        <div v-if="selectedCustomer?.id"
                             :class="['col-span-12 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[32px] 2xl:h-[40px]', selectedCustomer?.id ? 'lg:col-span-8' : 'lg:col-span-12']">
                          <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                               :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                            <div
                                class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="currentColor"
                                      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-1">
                            <div class="flex flex-col">
                              <div class="flex flex-row gap-6 text-xs">
                                <div>{{ selectedCustomer.name || selectedCustomer.storeID }}</div>
                                <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                                </div>
                              </div>
                              <div class="flex flex-row w-max text-xs text-slate-400">
                                <div>{{ selectedCustomer.discordUsername ?? "ــــــ" }}</div>
                                <div>
                                  {{
                                    "&nbsp- " + (selectedCustomer.discordDisplayName ? selectedCustomer.discordDisplayName : "ــــــ")
                                  }}
                                </div>
                              </div>
                            </div>
                            <div class="flex flex-col text-xs">
                              <div>{{ selectedCustomer.verificationState ?? "ــــــ" }}</div>
                              <div>
                                {{
                                  selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                          :class="['col-span-12 order-1 lg:order-2 flex flex-col gap-0.5 text-xs 2xl:text-sm', selectedCustomer?.id && canChangeCustomer ? 'lg:col-span-12' : 'lg:col-span-8']">
                        <FloatLabel variant="on">
                          <InputText id="takers" v-model="order.takers" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                          <label for="takers" class="!bg-slate-900">Takers</label>
                        </FloatLabel>
                      </div>
                    </div>
                  </div>
                  <HotOrders :hotOrders="hotOrders" :orderID="order.id" @rowClick="showUpdateHotOrderModal"
                             @add="showAddHotOrderModal" @showLogModal="showHotOrderLog"
                             :loadingHotOrders="loadingHotOrders"/>
                </TabPanel>
                <TabPanel :unmount="false"
                          class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2 pt-4">
                  <!-- Only changes -->
                  <Attachments
                      id="editOrder"
                      :count="16"
                      :backend-images="order.attachments?.paymentReceipts ?? []"
                      :only-changes="true"
                      v-model:uploading="isUploadingAttachment"
                      @update:payload="val => (attachments = val)"
                  />
                </TabPanel>
              </div>
            </div>
            <div class="col-span-1 flex flex-col lg:h-full overflow-hidden">
              <hr class="border-0 h-0.5 bg-slate-800">
              <div class="flex flex-col lg:h-full overflow-hidden gap-4 pt-4">
                <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm lg:h-full">
                  <FloatLabel variant="on" class="lg:h-full">
                    <Textarea v-auto-dir id="notes" v-model="order.notes" rows="5" style="resize: none"
                              class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                    <label for="notes" class="!bg-slate-900">Notes</label>
                  </FloatLabel>
                </div>
                <hr v-if="canRemoveOrder || canUpdateOrder" class="border-0 h-0.5 bg-slate-800">
                <div v-if="canRemoveOrder || canUpdateOrder" class="grid gap-2"
                     :class="[canRemoveOrder && canUpdateOrder ? 'grid-cols-2' : 'grid-cols-1']">
                  <button
                      v-if="canRemoveOrder"
                      @click="onDelete" class="btn btn-outline-dark py-2 text-xs 2xl:text-sm">
                      <span v-if="loading"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Delete order
                  </button>

                  <button v-if="canUpdateOrder" @click="onSave"
                          class="btn btn-outline-primary py-2 text-xs 2xl:text-sm" :disabled="saving">
                      <span v-if="saving"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                        <Loading/>
                      </span>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <UpdateHotOrder v-if="isShowUpdateHotOrderModal" @closeModal="closeUpdateHotOrderModal"
                    @updatedHotOrder="getHotOrders"
                    :selectedHotOrder="selectedHotOrder"/>
    <AddHotOrder v-if="isShowAddHotOrderModal" @closeModal="closeAddHotOrderModal" @addedNewHotOrder="getHotOrders"
                 :orderID="order.id" :orderTitle="order.title"/>
    <HotOrderLog v-if="isShowHotOrderLog" @closeModal="closeHotOrderLog" :hotOrder="selectedHotOrder"/>
    <CustomerProfile v-if="isShowCustomerProfileModal" :userID="customerStore.selectedCustomer.id"/>
  </div>
</template>

<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-6 2xl:h-8 w-6 2xl:w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

:deep(.p-floatlabel:has(input.has-error) label) {
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}

:deep(.p-floatlabel:has(input.has-error)) {
  @apply !border !border-red-300 !rounded-md
}

:deep(.p-inputgroup .p-floatlabel.has-error) {
  @apply !border-r-0 !rounded-r-none
}

:deep(.p-inputgroup:has(.p-floatlabel.has-error) .p-inputgroupaddon) {
  @apply !border !border-red-300 !rounded-md !border-l-0 !rounded-l-none
}


:deep(.p-select-clear-icon) {
  @apply size-3
}

:deep(.p-select-dropdown) {
  @apply w-6
}


/*------------*/
/*:deep(.custom-select.p-floatlabel label) {
  top: 0 !important;
  transform: translateY(-50%);
  @apply bg-slate-900 py-0 px-[0.125rem];
}*/

/*:deep(.p-floatlabel.custom-select label) {
  font-size: 0.75rem
}*/

:deep(.p-floatlabel.custom-select.has-error label) {
  /*--p-floatlabel-color: rgb(252, 165, 165);*/
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}


/*:root {
  --p-select-option-focus-background: #521111;
  --p-select-option-focus-color: #707007;
}*/
</style>
